import { Alert, Button, Input, InputNumber, Segmented, Select, Switch } from "antd";
import { Download, Plus, Trash2 } from "lucide-react";
import { tableLabel } from "../../../../engine";
import { useDownload } from "../../data/useDownload";
import { DeleteOutput } from "../DeleteControls";
import { ColumnSelect } from "./ParamForm";

// `ready`: the output passed its checks on the latest run, so it can be downloaded.
const OutputInspector = ({ output, workflow, run, ready, errors, dispatch }) => {
  const [downloading, download] = useDownload();
  const update = (changes) => dispatch({ type: "updateOutput", id: output.id, changes });
  const updateSheet = (index, changes) => update({ sheets: output.sheets.map((s, i) => (i === index ? { ...s, ...changes } : s)) });
  const tableChoices = [...workflow.sources, ...workflow.steps].map((item) => ({ value: item.id, label: tableLabel(workflow, item.id) }));
  const sheets = output.format === "xlsx" ? output.sheets : output.sheets.slice(0, 1);

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-orange-600">Output</div>
        <Input variant="borderless" className="!px-0 text-lg font-semibold" value={output.fileName} onChange={(e) => update({ fileName: e.target.value })} />
      </div>

      {errors.map((error) => <Alert key={error} type="error" showIcon message={error} />)}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs font-medium text-gray-600 mb-1">Format</div>
          <Segmented block value={output.format} onChange={(format) => update({ format })} options={[{ value: "xlsx", label: "Excel" }, { value: "csv", label: "CSV" }]} />
        </div>
        <div>
          <div className="text-xs font-medium text-gray-600 mb-1">Date format</div>
          <Input placeholder="yyyy-MM-dd" value={output.dateFormat ?? ""} onChange={(e) => update({ dateFormat: e.target.value || undefined })} />
        </div>
      </div>

      {sheets.map((sheet, index) => {
        const table = run?.tables[sheet.from];
        const custom = Array.isArray(sheet.columns) && sheet.columns.length > 0;
        const setColumns = (columns) => updateSheet(index, { columns });
        return (
          <div key={index} className="rounded-md border border-gray-200 p-3 space-y-3">
            <div className="flex items-center gap-2">
              {output.format === "xlsx" ? (
                <Input size="small" addonBefore="Sheet" value={sheet.name} onChange={(e) => updateSheet(index, { name: e.target.value })} />
              ) : (
                <span className="text-xs font-medium text-gray-600">Contents</span>
              )}
              {sheets.length > 1 && (
                <button type="button" className="text-gray-400 hover:text-red-500" onClick={() => update({ sheets: output.sheets.filter((_, i) => i !== index) })}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <div>
              <div className="text-xs font-medium text-gray-600 mb-1">Data from</div>
              <Select className="w-full" value={sheet.from} options={tableChoices} onChange={(from) => updateSheet(index, { from, columns: undefined })} />
            </div>
            <label className="flex items-center justify-between text-xs font-medium text-gray-600">
              Write a header row
              <Switch size="small" checked={sheet.writeHeader !== false} onChange={(writeHeader) => updateSheet(index, { writeHeader })} />
            </label>
            <Segmented
              block
              size="small"
              value={custom ? "custom" : "all"}
              onChange={(mode) =>
                setColumns(
                  mode === "all"
                    ? undefined
                    : (table?.columns || []).map((c, i) => ({ position: i + 1, header: c.name, column: c.name })),
                )
              }
              options={[{ value: "all", label: "All columns" }, { value: "custom", label: "Choose & position" }]}
            />
            {custom && (
              <div className="space-y-1">
                <div className="grid grid-cols-[56px_1fr_1fr_20px] gap-1 text-[11px] text-gray-500">
                  <span>Position</span>
                  <span>Header</span>
                  <span>Column</span>
                </div>
                {sheet.columns.map((column, i) => (
                  <div key={i} className="grid grid-cols-[56px_1fr_1fr_20px] gap-1 items-center">
                    <InputNumber size="small" min={1} value={column.position} onChange={(position) => setColumns(sheet.columns.map((c, j) => (j === i ? { ...c, position } : c)))} />
                    <Input size="small" value={column.header ?? ""} onChange={(e) => setColumns(sheet.columns.map((c, j) => (j === i ? { ...c, header: e.target.value } : c)))} />
                    <ColumnSelect table={table} value={column.column} onChange={(name) => setColumns(sheet.columns.map((c, j) => (j === i ? { ...c, column: name, header: c.header || name } : c)))} />
                    <button type="button" className="text-gray-400 hover:text-red-500" onClick={() => setColumns(sheet.columns.filter((_, j) => j !== i))}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
                <Button
                  size="small"
                  icon={<Plus size={12} />}
                  onClick={() => setColumns([...sheet.columns, { position: Math.max(0, ...sheet.columns.map((c) => Number(c.position) || 0)) + 1, header: "" }])}
                >
                  Add column
                </Button>
              </div>
            )}
          </div>
        );
      })}

      {output.format === "xlsx" && (
        <Button
          size="small"
          icon={<Plus size={14} />}
          onClick={() => update({ sheets: [...output.sheets, { name: `Sheet${output.sheets.length + 1}`, from: output.sheets[0]?.from, writeHeader: true }] })}
        >
          Add sheet
        </Button>
      )}

      <div className="flex justify-between border-t pt-3">
        <Button type="primary" icon={<Download size={14} />} disabled={!ready} loading={downloading} onClick={() => download("editor", output)}>
          Download preview
        </Button>
        <DeleteOutput output={output}>
          <Button danger icon={<Trash2 size={14} />}>Remove</Button>
        </DeleteOutput>
      </div>
    </div>
  );
};

export default OutputInspector;
